<template>
  <div class="bg-neutral-background-weak flex flex-col w-full h-full">
    <div
      v-if="!hasCompletedDetails"
      class="flex flex-col items-center justify-center min-h-[650px]"
    >
      <FontAwesomeIcon
        :icon="['fak', 'search']"
        :style="{ height: '40px', width: '40px', color: 'currentColor' }"
        class="mb-16 text-neutral-accent"
      />
      <span class="text-neutral-text-strong font-bold text-lg mb-4">
        We'll show your records here
      </span>
      <span class="text-neutral-text text-sm">
        Complete this step to preview your data
      </span>
    </div>
    <div v-else class="flex flex-col h-full min-h-[450px]">
      <div class="flex items-center justify-between p-16">
        <div class="flex items-center mr-16">
          <div class="text-neutral-text-strong font-bold mr-8">
            Preview Records
          </div>
          <BittsTag
            v-if="popCountLoaded"
            color="secondary"
            size="small"
            class="c-preview-count-tag"
            :text="previewCountText"
          />
        </div>
        <div class="flex">
          <BittsButton
            text="Refresh"
            variant="outline"
            type="neutral"
            class="bg-neutral-background-weak mr-6"
            :disabled="hasInvalidFilters"
            @click="emit('refresh')"
          />
          <BittsTooltip placement="topRight">
            <BittsButton
              :center-icon="['fak', isExpanded ? 'collapse' : 'expand']"
              variant="outline"
              type="neutral"
              class="bg-neutral-background-weak"
              :aria-label="isExpanded ? 'Collapse' : 'Expand'"
              @click="emit('toggle-expand')"
            />
            <template #title>
              {{ isExpanded ? 'Collapse View' : 'Expand View' }}
            </template>
          </BittsTooltip>
        </div>
      </div>
      <div class="px-16 pb-16">
        <PopulationPreview
          :key="previewKey"
          :population="population"
          :source-filter-expressions="sourceFilterExpressions"
          :offline-partner-uuid="offlinePartnerUuid"
          :is-syncing="isSyncing"
          :is-open-data="isOpenData"
          @preview-population-count="
            (count) => emit('preview-population-count', count)
          "
          @user-refresh-preview="emit('user-refresh-preview')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { intComma } from 'humanize-plus';
import { computed } from 'vue';

import type {
  Population,
  PopulationPreviewPayload,
  PopulationStats,
} from '@/types/populations';

import PopulationPreview, {
  OPEN_DATA_PREVIEW_LIMIT,
} from './PopulationPreview.vue';

const props = defineProps<Props>();

const emit = defineEmits<{
  refresh: [];
  'toggle-expand': [];
  'preview-population-count': [count: number];
  'user-refresh-preview': [];
}>();

type Props = {
  hasCompletedDetails: boolean;
  popCountLoaded: boolean;
  populationPreviewCount: number;
  hasInvalidFilters: boolean;
  previewKey: number;
  population:
    | (Population & {
        meta?: PopulationStats;
        isEmpty?: boolean;
        tag?: string;
      })
    | PopulationPreviewPayload
    | null;
  sourceFilterExpressions: Record<string, unknown>;
  offlinePartnerUuid?: string;
  isExpanded: boolean;
  isSyncing: boolean;
  isOpenData?: boolean;
};

const previewCountText = computed(() => {
  const total = props.populationPreviewCount;
  const displayed = Math.min(OPEN_DATA_PREVIEW_LIMIT, total);
  if (props.isOpenData && total > displayed) {
    return `${displayed} of ${intComma(total)}`;
  }
  return intComma(total);
});
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";
.c-preview-count-tag :deep(.ant-tag) {
  @apply py-[2px]!;
}
</style>
